import { clsx } from 'clsx';
import { useCallback, useRef, type KeyboardEvent, type ClipboardEvent } from 'react';

export interface OTPInputProps {
  /** Number of OTP digits */
  length?: number;
  /** Current OTP value string */
  value: string;
  /** Called with the updated OTP string */
  onChange: (value: string) => void;
  /** Additional className for the container */
  className?: string;
}

/**
 * OTPInput - Atom component for one-time password digit entry
 *
 * Renders individual input boxes for each digit of an OTP code.
 * Supports keyboard navigation (arrow keys, backspace) and paste.
 */
export function OTPInput({ length = 4, value, onChange, className }: OTPInputProps) {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const focusInput = useCallback(
    (index: number) => {
      const clamped = Math.max(0, Math.min(index, length - 1));
      inputRefs.current[clamped]?.focus();
    },
    [length]
  );

  const handleChange = useCallback(
    (index: number, rawValue: string) => {
      const digits = rawValue.replace(/\D/g, '');

      if (digits.length > 1) {
        const filled = digits.slice(0, length);
        onChange(filled);
        focusInput(Math.min(filled.length, length - 1));
        return;
      }

      const digit = digits;
      const chars = value.split('');
      chars[index] = digit;
      const newValue = chars.join('').slice(0, length);
      onChange(newValue);

      if (digit && index < length - 1) {
        focusInput(index + 1);
      }
    },
    [value, length, onChange, focusInput]
  );

  const handleKeyDown = useCallback(
    (index: number, e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace' && !value[index] && index > 0) {
        focusInput(index - 1);
      } else if (e.key === 'ArrowLeft' && index > 0) {
        focusInput(index - 1);
      } else if (e.key === 'ArrowRight' && index < length - 1) {
        focusInput(index + 1);
      }
    },
    [value, length, focusInput]
  );

  const handlePaste = useCallback(
    (e: ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
      if (pasted) {
        onChange(pasted);
        focusInput(Math.min(pasted.length, length - 1));
      }
    },
    [length, onChange, focusInput]
  );

  return (
    <div className={clsx('flex gap-3', className)} role="group" aria-label="One-time password">
      {Array.from({ length }, (_, i) => (
        <input
          key={i}
          ref={(el) => {
            inputRefs.current[i] = el;
          }}
          type="text"
          inputMode="numeric"
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          value={value[i] ?? ''}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={handlePaste}
          className="font-sailec border-dorsia-border text-dorsia-black focus:border-dorsia-border-focus h-14 w-14 rounded-lg border text-center text-2xl font-medium outline-none transition-colors"
          aria-label={`Digit ${i + 1}`}
        />
      ))}
    </div>
  );
}
